import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getDeliveryConfiguration,
  createDeliveryConfiguration,
  updateDeliveryConfiguration,
  deleteDeliveryConfiguration
} from '../services/api/customerDeliveryConfigurationApi';

/**
 * Custom hook for managing customer delivery configuration
 * 
 * @param {string} customerId - The ID of the customer
 * @returns {object} Delivery configuration state and operations
 */
export const useDeliveryConfiguration = (customerId) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasExistingApiKey, setHasExistingApiKey] = useState(false);
  const hasFetched = useRef(false);

  /**
   * Fetch delivery configuration for the customer
   */
  const fetchConfig = useCallback(async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getDeliveryConfiguration(customerId);
      setConfig(response.data);
      setHasExistingApiKey(response.data.hasApiSigningKey || false);
    } catch (err) {
      if (err.response?.status === 404) {
        // No configuration exists yet - this is not an error
        setConfig(null);
        setHasExistingApiKey(false);
      } else {
        setError(err.response?.data?.message || 'Failed to load delivery configuration');
      }
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  /**
   * Create new delivery configuration
   * @param {object} data - Configuration data
   */
  const createConfig = async (data) => {
    try {
      setError(null);
      const response = await createDeliveryConfiguration(customerId, data);
      setConfig(response.data);
      setHasExistingApiKey(!!data.apiSigningKey);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create delivery configuration';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Update existing delivery configuration
   * @param {object} data - Updated configuration data
   */
  const updateConfig = async (data) => {
    try {
      setError(null);
      const response = await updateDeliveryConfiguration(customerId, data);
      setConfig(response.data);
      if (data.apiSigningKey) {
        setHasExistingApiKey(true);
      }
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update delivery configuration';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Delete delivery configuration
   */
  const deleteConfig = async () => {
    try {
      setError(null);
      await deleteDeliveryConfiguration(customerId);
      setConfig(null);
      setHasExistingApiKey(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete delivery configuration';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    if (!hasFetched.current && customerId) {
      hasFetched.current = true;
      fetchConfig();
    }
  }, [customerId, fetchConfig]);

  // Reset hasFetched when customerId changes
  useEffect(() => {
    hasFetched.current = false;
  }, [customerId]);

  return {
    config,
    loading,
    error,
    hasExistingApiKey,
    fetchConfig,
    createConfig,
    updateConfig,
    deleteConfig,
    clearError: () => setError(null)
  };
};
